import { Field, FieldInputProps, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import useAppToasts, { useShowError } from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useChangePassword from "@/features/my-account/hooks/use-change-password";
import useGetVerificationCode from "../hooks/useGetVerificationCode";
import useVerifyCode, { VerifyCodeResponse } from "../hooks/useVerifyCode";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
} from "@chakra-ui/react";
import AppSelect from "@/features/line-sheet-sets/app-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import { checkPasswordComplexity } from "@/features/auth/utils/password-utils";
import useMobileValidation from "@/features/auth/hooks/useMobileValidation";

export interface ForgotPasswordStep1FormFields {
  countryCode: string;
  mobileNumber: string;
  mobileVerificationCode: string;
  tempToken: string;
}

interface ForgotPasswordStep1FormProps {
  initialValue: ForgotPasswordStep1FormFields;
  onSubmit: (formValues: ForgotPasswordStep1FormFields) => void;
}

export default function ForgotPasswordStep1Form({
  initialValue,
  onSubmit,
}: ForgotPasswordStep1FormProps) {
  const { t, tTitle, tSentence } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const { navigate, searchParams } = useRouterHelper();
  const { withMessage, formT } = useYupHelper({
    // prefix: "forgot_password_form",
    prefix: "sign_up_form",
    ns: "auth",
  });

  const [isMobileVerificationCodeSent, setIsMobileVerificationCodeSent] =
    useState<boolean>(false);
  const {
    fire: fireGetVerificationCode,
    isLoading: isGetVerificationCodeLoading,
    data: getVerificationCodeData,
    error: getVerificationCodeError,
  } = useGetVerificationCode();

  const {
    fire: fireVerifyCode,
    isLoading: isVerifyCodeLoading,
    data: verifyCodeData,
    error: verifyCodeError,
  } = useVerifyCode();

  useEffect(() => {
    if (verifyCodeError) {
      console.error("verifyCodeError:", verifyCodeError);
      showError(verifyCodeError, {
        id: "SING_UP_ERROR",
        i18n: {
          keyPrefix: "sign_up_form.button.verify_code.error",
          ns: "auth",
        },
      });
    }
  }, [verifyCodeError, showError]);

  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isCountdownActive, setIsCountdownActive] = useState<boolean>(false);
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
    useState<boolean>(true);
  const startCountdown = (duration: number) => {
    setRemainingTime(duration);
    setIsCountdownActive(true);
    setIsContinueButtonDisabled(false);
  };
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCountdownActive && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prev) => prev - 1);
      }, 1000);
    } else if (remainingTime <= 0) {
      setIsCountdownActive(false);
      setIsContinueButtonDisabled(true);
    }
    return () => clearInterval(timer);
  }, [isCountdownActive, remainingTime]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  useEffect(() => {
    if (getVerificationCodeError) {
      console.error("getVerificationCodeError:", getVerificationCodeError);
    }
    if (getVerificationCodeData) {
      if (getVerificationCodeData.message === "success") {
        setIsMobileVerificationCodeSent(true);
        startCountdown(180);
      }
    }
  }, [getVerificationCodeData, getVerificationCodeError]);

  const {
    isLoading: isChangePasswordLoading,
    data: getChangePasswordData,
    error: getChangePasswordError,
    fire: fireChangePassword,
  } = useChangePassword();

  const validateSchema = useMemo(() => {
    return Yup.object({
      mobileNumber: Yup.string()
        .required(withMessage.required)
        .min(10, withMessage.min),
      mobileVerificationCode: Yup.string()
        .required(tSentence("auth:mobile_verification_code_required_error"))
        .matches(
          /^\d+$/,
          tSentence("auth:mobile_verification_code_numeric.error")
        )
        .length(4, tSentence("auth:mobile_verification_code_length.error"))
        .test(
          "isCodeExpired",
          tSentence(
            "auth:sign_up_form.field.mobile_verification_code.error.code_expired"
          ),
          () => !(isMobileVerificationCodeSent && remainingTime <= 0)
        ),
      tempToken: Yup.string().required(withMessage.required),
    });
  }, [
    withMessage.min,
    withMessage.required,
    remainingTime,
    tSentence,
    isMobileVerificationCodeSent,
  ]);

  useShowError(getChangePasswordError, (e) => showError(e, {
    id: "CHANGE_PASSWORD_ERROR",
    i18n: {
      keyPrefix: "sign_in.error",
      ns: "auth",
    },
  }));

  const [formValues, setFormValues] =
    useState<ForgotPasswordStep1FormFields>(initialValue);
  const [onSubmitCalled, setOnSubmitCalled] = useState<boolean>(false);

  useEffect(() => {
    if (formValues && onSubmitCalled) {
      setOnSubmitCalled(false);
      onSubmit(formValues);
    }
  }, [formValues, onSubmitCalled, onSubmit]);

  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validateSchema}
      onSubmit={(values, actions) => {
        actions.setSubmitting(true);
        onSubmit(values);
        return;
      }}
    >
      {(props: FormikProps<ForgotPasswordStep1FormFields>) => {
        if (getChangePasswordError && props.isSubmitting) {
          props.setSubmitting(false);
        }
        return (
          <Form
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "32px",
            }}
          >
            <Box display="flex" flexDirection="column" gap="32px">
              <Box
                display="flex"
                flexDirection="column"
                className={"flex flex-col gap-5"}
              >
                <Field name="mobileNumber">
                  {({
                    field,
                    form,
                  }: {
                    field: FieldInputProps<string>;
                    form: FormikProps<ForgotPasswordStep1FormFields>;
                  }) => (
                    <FormControl
                      isRequired
                      isInvalid={
                        !!form.errors.mobileNumber && form.touched.mobileNumber
                      }
                    >
                      <FormLabel display="flex" m={"0"}>
                        <Text
                          color="#6F6F6F"
                          fontSize={"12px"}
                          fontWeight={"400"}
                        >
                          {tTitle("sign_up_form.field.mobile_number", {
                            ns: "auth",
                          })}
                        </Text>
                      </FormLabel>

                      <FormControl>
                        <Flex flexDirection={"row"} gap={"8px"}>
                          <AppSelect<string>
                            value={form.values.countryCode}
                            name={"KR"}
                            width={"64px"}
                            height={"40px"}
                            options={[
                              { value: "+82", name: "KR" },
                              { value: "+39", name: "IT" },
                            ]}
                            isClearable={false}
                            onSelect={(value) => {
                              form.setFieldValue("countryCode", value);
                            }}
                          />

                          <Input
                            {...field}
                            type={"tel"}
                            inputMode={"numeric"}
                            onChange={(e) => {
                              form.setFieldValue(
                                field.name,
                                e.target.value.replace(/[^0-9]/g, "")
                              );
                            }}
                            fontSize={"12px"}
                            placeholder={tSentence(
                              "auth:sign_up_form.field.mobile_number.placeholder"
                            )}
                          />
                        </Flex>
                      </FormControl>
                      <Flex h="24px" flexDirection="row" alignItems={"center"}>
                        <FormErrorMessage m="0" fontSize={"12px"}>
                          {form.errors.mobileNumber}
                        </FormErrorMessage>
                      </Flex>
                    </FormControl>
                  )}
                </Field>
                {!isMobileVerificationCodeSent ? (
                  <Box textAlign={"center"}>
                    <AppButton
                      isLoading={isGetVerificationCodeLoading}
                      width={"100%"}
                      isDisabled={props.values.mobileNumber.length < 10}
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        await fireGetVerificationCode(
                          {},
                          {
                            mobileNumber: props.values.mobileNumber,
                          }
                        );
                      }}
                    >
                      {tTitle(
                        "auth:sign_up_form.button.get_verification_code.do"
                      )}
                    </AppButton>
                  </Box>
                ) : (
                  <Box textAlign={"center"}>
                    <AppButton
                      isLoading={isGetVerificationCodeLoading}
                      width={"100%"}
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        await fireGetVerificationCode(
                          {},
                          {
                            mobileNumber: props.values.mobileNumber,
                          }
                        );
                      }}
                    >
                      {tTitle("auth:sign_up_form.button.resend_code.do", {
                        remainingTime: formatTime(remainingTime),
                      })}
                    </AppButton>
                  </Box>
                )}
              </Box>

              <Box display="flex" flexDirection="column">
                {isMobileVerificationCodeSent && (
                  <Field name="mobileVerificationCode">
                    {({
                      field,
                      form,
                    }: {
                      field: FieldInputProps<string>;
                      form: FormikProps<ForgotPasswordStep1FormFields>;
                    }) => (
                      <FormControl
                        isRequired
                        isInvalid={
                          !!form.errors.mobileVerificationCode &&
                          form.touched.mobileVerificationCode
                        }
                      >
                        <FormLabel display="flex" m={"0"}>
                          <Text
                            color="#6F6F6F"
                            fontSize={"12px"}
                            fontWeight={"400"}
                          >
                            {tTitle(
                              "auth:sign_up_form.field.mobile_verification_code.label"
                            )}
                          </Text>
                        </FormLabel>

                        <FormControl>
                          <Flex flexDirection={"row"} gap={"8px"}>
                            <Input
                              {...field}
                              type={"text"}
                              inputMode={"numeric"}
                              placeholder={tSentence(
                                "auth:sign_up_form.field.mobile_verification_code.placeholder"
                              )}
                              fontSize={"12px"}
                            />
                          </Flex>
                        </FormControl>
                        <Flex
                          h="24px"
                          flexDirection="row"
                          alignItems={"center"}
                        >
                          <FormErrorMessage
                            m="0"
                            fontSize={"12px"}
                            whiteSpace="nowrap"
                          >
                            {form.errors.mobileVerificationCode}
                          </FormErrorMessage>
                        </Flex>
                      </FormControl>
                    )}
                  </Field>
                )}
                {isMobileVerificationCodeSent && (
                  <Box textAlign={"center"}>
                    <AppButton
                      isLoading={isVerifyCodeLoading}
                      width={"100%"}
                      isDisabled={
                        isContinueButtonDisabled ||
                        !!props.errors.mobileVerificationCode
                      }
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        const promise: VerifyCodeResponse =
                          await fireVerifyCode(
                            {},
                            {
                              mobileNumber: props.values.mobileNumber,
                              verificationCode:
                                props.values.mobileVerificationCode,
                            }
                          );
                        setFormValues({
                          ...formValues,
                          ...props.values,
                          tempToken: promise.tempToken,
                        });
                        setOnSubmitCalled(true);
                      }}
                    >
                      {tTitle("auth:sign_up_form.button.verify_code.do")}
                    </AppButton>
                  </Box>
                )}
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
